import { Injectable } from '@angular/core';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { chain, isEqual, join, map, toLower, filter, isEmpty } from 'lodash';
import { I18n } from '@app/i18n/strings-keys';
import { stringCaseInsensitiveComparator } from '@app/shared/common';
import { Observable, observableOf, rx } from '@app/shared/rx/rx-wrapper';
import {
  COLUMN_SELECTOR_ID,
  HAWKER_PROFILE_ACTION_BUTTONS,
  IHawkerProfile,
  SelectOption,
} from '@app/shared/types';
import { CELL_FILTER } from '@app/shared/types/enums/cell-filters.enum';
import { CELL_RENDERER } from '@app/shared/types/enums/cell-renderer.enum';
import { ENTITY_TYPES } from '@app/shared/types/enums/entityTypes.enum';
import { ISite } from '@app/shared/types/portal/company/site.type';
import { IXVIListColumn } from '@app/shared/types/ui/xux-grid/xux-grid.type';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import { BaseInjectService } from '../base';
const NO_ASSIGNED_COMMISSION_RATE_VALUE = 'NO_ASSIGNED_RATE';
@Injectable()
export class HawkerProfileColumnService extends BaseInjectService {
  public buildColumns(options: {
    sites$: Observable<ISite[]>;
    hawkerList$: Observable<IHawkerProfile[]>;
    handleAssignRateClick: Function;
  }): (IXVIListColumn | ColDef)[] {
    const entityType = ENTITY_TYPES.HAWKER_PROFILE;

    return [
      {
        path: 'virtual.lastName',
        title: I18n.Forms.GridForm.LastName,
        width: 150,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        sort: 'asc',
        cellRenderer: CELL_RENDERER.link_renderer,
        comparator: stringCaseInsensitiveComparator,
        cellRendererParams: {
          entityType,
        },
        defaultView: true,
      },
      {
        path: 'virtual.firstName',
        title: I18n.Forms.GridForm.FirstName,
        width: 150,
        comparator: stringCaseInsensitiveComparator,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
        },
        defaultView: true,
      },
      {
        path: 'virtual.badgeNumber',
        title: I18n.Forms.GridForm.BadgeNumber,
        width: 180,
        sortable: true,
        comparator: stringCaseInsensitiveComparator,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        defaultView: true,
      },
      {
        path: 'commission_rates',
        title: I18n.EntityNames.CommissionRate,
        width: 300,
        filterType: CELL_FILTER.entity,
        filterParams: {
          quickFilter: true,
          suppressSelectAllControl: true,
          filterOptions: ['containsAny', 'containsAll', 'notContains'],
          values: this.getCommissionRateFilterValuesObs({
            hawkerList$: options.hawkerList$,
          }),
          filterValueGetter: this.commissionRateFilterValueGetter,
          filterComparator: (
            currentOption: SelectOption,
            compareOption: SelectOption,
          ): number => {
            // Special handling for "No Rate Assigned" option, always appears first
            if (
              isEqual(currentOption.value, NO_ASSIGNED_COMMISSION_RATE_VALUE)
            ) {
              return -1;
            }
            if (
              isEqual(compareOption.value, NO_ASSIGNED_COMMISSION_RATE_VALUE)
            ) {
              return 1;
            }

            // Sort other options alphabetically by text (case-insensitive)
            const currentText = currentOption.text;
            const compareText = compareOption.text;

            return stringCaseInsensitiveComparator(currentText, compareText);
          },
        },
        // This ensures only primary rates are matched when user selects a filter option
        filterValueGetter: (params: ValueGetterParams): string[] => {
          const primaryRates = filter(
            params.data.commission_rates,
            (rate) => rate.is_primary,
          );

          return map(primaryRates, 'name');
        },
        valueGetter: (params) => {
          const primaryRates = filter(
            params.data.commission_rates,
            (rate) => rate.is_primary,
          );
          if (isEmpty(primaryRates)) {
            return this.i18nService.t(
              I18n.Forms.HawkerProfileForm.AssignCommissionRate,
            );
          }

          return map(primaryRates, 'name').join(', ');
        },
        cellClass: 'cursor-pointer',
        cellStyle: { textDecoration: 'underline' },
        onCellClicked: (params) => {
          options.handleAssignRateClick([params.data]);
        },
        defaultView: true,
      },
      {
        path: 'virtual.siteName',
        title: I18n.Sites.SiteName,
        width: 250,
        comparator: stringCaseInsensitiveComparator,
        filterType: CELL_FILTER.entity,
        filterParams: {
          quickFilter: true,
          suppressSelectAllControl: true,
          filterOptions: ['containsAny', 'containsAll', 'notContains'],
          values: this.getSiteFilterValuesObs({
            sites$: options.sites$,
            path: 'name',
          }),
          filterValueGetter: this.siteFilterValueGetter,
        },
        valueFormatter: (params) =>
          join(map(params.data.virtual?.sites, 'name'), ', '),
        filterValueGetter: (params) => {
          return map(params.data.virtual?.sites, 'name');
        },
        defaultView: true,
      },
      {
        path: 'virtual.siteId',
        title: I18n.Sites.SiteId,
        filterType: CELL_FILTER.entity,
        filterParams: {
          quickFilter: true,
          filterOptions: ['containsAny', 'containsAll', 'notContains'],
          suppressSelectAllControl: true,
          values: this.getSiteFilterValuesObs({
            sites$: options.sites$,
            path: 'store_number',
          }),
          filterValueGetter: this.siteFilterValueGetter,
        },
        valueFormatter: (params) =>
          join(map(params.data.virtual?.sites, 'store_number'), ', '),
        filterValueGetter: (params) => {
          return map(params.data.virtual?.sites, 'store_number');
        },
      },
      {
        path: 'virtual.employeeCorporateCode',
        title: I18n.Forms.HawkerProfileForm.EmployeeCorporateCode,
        width: 220,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRendererParams: {
          entityType,
        },
        sortable: true,
        comparator: stringCaseInsensitiveComparator,
      },
      {
        path: COLUMN_SELECTOR_ID,
        width: 50,
        title: '',
        defaultView: true,
        cellRenderer: CELL_RENDERER.common_actions_renderer,
        cellRendererParams: {
          entityType,
          actionButtons: HAWKER_PROFILE_ACTION_BUTTONS,
        },
        pinned: detectMobile() ? false : 'right',
        lockPinned: true,
      },
    ];
  }

  protected getSiteFilterValuesObs(options: {
    sites$: Observable<ISite[]>;
    path: string;
  }) {
    return options.sites$.pipe(
      rx.map((sites) =>
        map(sites, (site) => ({
          text: site?.[options.path],
          value: site.id,
        })),
      ),
    );
  }

  protected getCommissionRateFilterValuesObs(options: {
    hawkerList$: Observable<IHawkerProfile[]>;
  }): Observable<SelectOption[]> {
    return options.hawkerList$.pipe(
      // Use switchMap to ensure filter updates when hawkerList$ changes
      rx.switchMap((hawkerList) => {
        const commissionRateOptions = chain(hawkerList)
          .flatMap((hawker) => hawker.commission_rates)
          .filter((rate) => rate.is_primary) // Only include primary rates
          .uniqBy('entity_id')
          .map((rate) => ({
            text: rate.name,
            value: rate.entity_id,
          }))
          .sortBy((option) => toLower(option.text))
          .value();

        return observableOf([
          {
            text: this.i18nService.t(
              I18n.Forms.HawkerProfileForm.NoAssignedRate,
            ),
            value: NO_ASSIGNED_COMMISSION_RATE_VALUE,
          },
          ...commissionRateOptions,
        ]);
      }),
      rx.debounceTime(300), // Prevent excessive updates
      rx.distinctUntilChanged((prev, curr) => {
        // Only emit when values actually change
        const prevValues = map(prev, 'value').sort();
        const currValues = map(curr, 'value').sort();

        return isEqual(prevValues, currValues);
      }),
      rx.shareReplay(1), // Share subscription among multiple consumers
    );
  }

  protected siteFilterValueGetter(data) {
    return map(data.virtual?.sites, 'id');
  }

  /**
   * @param data - Row data containing commission_rates array
   * @returns Array of entity IDs for primary rates, or special "No Rate Assigned" value if empty
   */
  protected commissionRateFilterValueGetter(data) {
    // Filter to get only primary commission rates
    const primaryRates = filter(
      data.commission_rates,
      (rate) => rate.is_primary,
    );
    const entityIds = map(primaryRates, 'entity_id');

    // If no primary commission rates assigned, return special value for "No Rate Assigned" filter
    return isEmpty(entityIds) ? [NO_ASSIGNED_COMMISSION_RATE_VALUE] : entityIds;
  }
}
